import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { TPersonalDataMuiColumn, TTableFilters, TTableSorter } from '../types.ts';
import {
  personalDataMuiEmptyCellSx,
  personalDataMuiTableContainerSx,
  personalDataMuiTableSx,
} from '../styles.ts';
import { PersonalDataMuiTableHeaderRow } from './PersonalDataMuiTableHeaderRow.tsx';
import { PersonalDataMuiTableBodyRow } from './PersonalDataMuiTableBodyRow.tsx';
import { getNextOrder } from '../utils/utils.ts';

const getRecordValue = (record: object, key: string): unknown =>
  (record as Record<string, unknown>)[key];

interface IPersonalDataMuiTableProps<TRecord extends object> {
  rowKey: string;
  columns: TPersonalDataMuiColumn<TRecord>[];
  dataSource: TRecord[];
  onChange?: (
    filters: TTableFilters,
    sorter: TTableSorter | TTableSorter[],
    extra: { action: string }
  ) => void;
  locale?: {
    emptyText?: React.ReactNode;
  };
}

export const PersonalDataMuiTable = <TRecord extends object>({
  rowKey,
  columns,
  dataSource,
  onChange,
  locale,
}: IPersonalDataMuiTableProps<TRecord>) => {
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [sorter, setSorter] = useState<TTableSorter>({});
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [activeFilterColumn, setActiveFilterColumn] =
    useState<TPersonalDataMuiColumn<TRecord> | null>(null);
  const [draftSelectedKeys, setDraftSelectedKeys] = useState<string[]>([]);
  const draftSelectedKeysRef = useRef<string[]>([]);

  const isFilterOpen = Boolean(filterAnchorEl);

  const sortedField = sorter.field;
  const sortedOrder = sorter.order;

  const rows = useMemo(() => (Array.isArray(dataSource) ? dataSource : []), [dataSource]);

  const handleSort = (column: TPersonalDataMuiColumn<TRecord>) => {
    if (!column.sorter) return;

    const currentOrder = sortedField === column.dataIndex ? sortedOrder : undefined;
    const nextOrder = getNextOrder(currentOrder);
    const nextSorter: TTableSorter = nextOrder ? { field: column.dataIndex, order: nextOrder } : {};

    setSorter(nextSorter);
    onChange?.(filters as TTableFilters, nextSorter as TTableSorter, {
      action: 'sort',
    });
  };

  const handleFilterOpen = (
    event: React.MouseEvent<HTMLElement>,
    column: TPersonalDataMuiColumn<TRecord>
  ) => {
    event.stopPropagation();
    setActiveFilterColumn(column);
    const nextDraftSelectedKeys = filters[column.dataIndex] || [];
    draftSelectedKeysRef.current = nextDraftSelectedKeys;
    setDraftSelectedKeys(nextDraftSelectedKeys);
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    setActiveFilterColumn(null);
    draftSelectedKeysRef.current = [];
    setDraftSelectedKeys([]);
  };

  const handleDraftSelectedKeysChange = useCallback((keys: string[]) => {
    draftSelectedKeysRef.current = keys;
    setDraftSelectedKeys(keys);
  }, []);

  const handleFilterConfirm = () => {
    if (!activeFilterColumn) return;

    const columnKey = activeFilterColumn.dataIndex;
    const selectedKeys = draftSelectedKeysRef.current;
    const nextFilters = {
      ...filters,
      [columnKey]: selectedKeys,
    };

    if (selectedKeys.length === 0) {
      delete nextFilters[columnKey];
    }

    setFilters(nextFilters);
    onChange?.(nextFilters as TTableFilters, sorter as TTableSorter, {
      action: 'filter',
    });

    handleFilterClose();
  };

  const activeFilterDropdown = activeFilterColumn?.filterDropdown;

  return (
    <TableContainer component={Paper} sx={personalDataMuiTableContainerSx}>
      <Table sx={personalDataMuiTableSx}>
        <TableHead>
          <PersonalDataMuiTableHeaderRow
            columns={columns}
            sortedField={sortedField}
            sortedOrder={sortedOrder}
            filters={filters}
            onSort={handleSort}
            onFilterOpen={handleFilterOpen}
          />
        </TableHead>

        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={personalDataMuiEmptyCellSx}>
                {locale?.emptyText || (
                  <Typography variant="body2" color="text.secondary">
                    No data
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((record, rowIndex) => (
              <PersonalDataMuiTableBodyRow
                key={String(getRecordValue(record, rowKey) ?? rowIndex)}
                rowKey={rowKey}
                rowIndex={rowIndex}
                record={record}
                columns={columns}
              />
            ))
          )}
        </TableBody>
      </Table>

      <Popover
        open={isFilterOpen}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {activeFilterDropdown &&
          activeFilterDropdown({
            setSelectedKeys: handleDraftSelectedKeysChange,
            selectedKeys: draftSelectedKeys,
            confirm: handleFilterConfirm,
          })}
      </Popover>
    </TableContainer>
  );
};
